import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export const AIEventDescriptionGenerator = ({
  organizationId,
  eventName,
  eventType,
  date,
  startTime,
  location,
  onDescriptionGenerated,
}) => {
  const { session } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  const canGenerate = eventName && eventType && date && location;
  
  const generate = async () => {
    if (!organizationId || !canGenerate) return;
    
    setLoading(true);
    setResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/event/generate-description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          event_name: eventName,
          event_type: eventType,
          date: date,
          start_time: startTime || '7:00 PM',
          location: location,
        }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        setResult(data);
        if (data.description) {
          onDescriptionGenerated(data.description);
        }
        toast.success('Description generated!');
      } else {
        toast.error(data.error || 'Failed to generate description');
      }
    } catch (error) {
      console.error('Generate error:', error);
      toast.error('Failed to generate description');
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };
  
  return (
    <div className="space-y-3">
      <Button
        type="button"
        onClick={generate}
        disabled={loading || !canGenerate}
        variant="outline"
        className={cn(
          "border-electric-ice-blue/30 text-electric-ice-blue hover:bg-electric-ice-blue/10",
          !canGenerate && "opacity-50 cursor-not-allowed"
        )}
        data-testid="ai-event-desc-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            Generate with AI
          </>
        )}
      </Button>
      
      {!canGenerate && (
        <p className="text-xs text-muted-text">
          Fill in event name, type, date, and location to enable AI generation
        </p>
      )}
      
      {result && (result.instagram_caption || result.email_subject) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {result.instagram_caption && (
            <button
              type="button"
              onClick={() => copyToClipboard(result.instagram_caption, 'instagram')}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-full text-xs text-purple-300 hover:border-purple-500/50 transition-colors"
              data-testid="copy-instagram-btn"
            >
              {copiedField === 'instagram' ? (
                <Check className="w-3 h-3" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
              Instagram Caption
            </button>
          )}
          
          {result.email_subject && (
            <button
              type="button"
              onClick={() => copyToClipboard(result.email_subject, 'email')}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-electric-ice-blue/10 border border-electric-ice-blue/30 rounded-full text-xs text-electric-ice-blue hover:border-electric-ice-blue/50 transition-colors"
              data-testid="copy-email-btn"
            >
              {copiedField === 'email' ? (
                <Check className="w-3 h-3" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
              Email Subject
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AIEventDescriptionGenerator;
